import { useEffect, MutableRefObject } from "react";
import { cacheManager } from "../../../utilities/cacheManager";
import { CACHE_CONSTANTS } from "../../../constants/cacheConstants";
import { LOCAL_STORAGE_KEY } from "../ez-quote.helpers";
import {
  Category,
  Attribute,
  Manufacturer,
  Model,
  Condition,
  SelectedAttribute,
  Item,
  OptionType,
  StoreOption,
} from "../ez-quote.types";

type CurrentItemState = {
  category: Category | null;
  attributes: { [key: string]: SelectedAttribute };
  condition: Condition | null;
  parentCategoryName?: string;
  goldTypeName?: string;
  itemTypeLabel?: string;
};

type DraftItemState = {
  category: Category | null;
  attributes: { [key: string]: SelectedAttribute };
  condition: Condition | null;
};

type CategoryItemStateMap = {
  [categoryId: number]: {
    attributes: { [key: string]: SelectedAttribute };
    condition: Condition | null;
  };
};

interface PersistenceSetters {
  setCurrentStep: (v: number) => void;
  setCategoryHistory: (v: Category[][]) => void;
  setSelectedCategoryParent: (v: Category | null) => void;
  setSelectedRootCategory: (v: Category | null) => void;
  setCurrentItem: (v: CurrentItemState) => void;
  setItems: (v: Item[]) => void;
  setIsEditing: (v: boolean) => void;
  setSelectedCondition: (v: string | null) => void;
  setCurrentAttributeIndex: (v: number) => void;
  setAttributes: (v: Attribute[]) => void;
  setManufacturerOptions: (v: Manufacturer[]) => void;
  setModelOptions: (v: Model[]) => void;
  setDraftItem: (v: DraftItemState) => void;
  setTotalEstimate: (v: { min: number; max: number }) => void;
  setquoteId: (v: string | null) => void;
  setBrand: (v: string | null) => void;
  setopenEstimateCard: (v: boolean) => void;
  setOpenExpertReviewAccordion: (v: boolean) => void;
  setCategoryItemState: (v: CategoryItemStateMap) => void;
  setZipCodeFromModal: (v: string | null) => void;
  setNearbyStores: (v: StoreOption[]) => void;
  setSelectedStoreOption: (v: StoreOption | null) => void;
  setEmailSendCount: (v: { [quoteId: string]: number }) => void;
  setGoldCurrentAttributeIndex: (v: number) => void;
  setGoldParentCategory: (v: Category | null) => void;
  setSelectedGoldItemType: (v: OptionType | null) => void;
  setCategories: (v: Category[]) => void;
  setHydrated: (v: boolean) => void;
  setError: (v: string | null) => void;
  setattributeError: (v: string | null) => void;
  resetForm: () => void;
  fetchCategories: () => void;
  fetchManufacturers: (categoryId: number) => void;
}

interface SaveState {
  hydrated: boolean;
  currentStep: number;
  categories: Category[];
  categoryHistory: Category[][];
  selectedCategoryParent: Category | null;
  selectedRootCategory: Category | null;
  currentItem: CurrentItemState;
  items: Item[];
  isEditing: boolean;
  selectedCondition: string | null;
  currentAttributeIndex: number;
  attributes: Attribute[];
  manufacturerOptions: Manufacturer[];
  modelOptions: Model[];
  draftItem: DraftItemState;
  totalEstimate: { min: number; max: number };
  quoteId: string | null;
  brand: string | null;
  openEstimateCard: boolean;
  openExpertReviewAccordion: boolean;
  categoryItemState: CategoryItemStateMap;
  zipCodeFromModal: string | null;
  nearbyStores: StoreOption[];
  selectedStoreOption: StoreOption | null;
  emailSendCount: { [quoteId: string]: number };
  goldCurrentAttributeIndex: number;
  goldParentCategory: Category | null;
  selectedGoldItemType: OptionType | null;
}

export function useLocalStoragePersistence(
  saveState: SaveState,
  setters: PersistenceSetters,
  // Set synchronously (before this hook's effects run) when the widget is showing
  // a saved quote by ezQuoteId (see ez-quote.tsx) — a ref, not state, because it
  // must be visible to this hook's own mount effect within the same effect flush.
  // Restoring/persisting the normal flow's state must never run in that mode: it
  // would either clobber the freshly-fetched saved quote or leak its data into a
  // later, unrelated session.
  isSavedQuoteModeRef: MutableRefObject<boolean>
) {
  const { hydrated, ...restState } = saveState;

  // --- Restore state from localStorage on mount with cache timeout ---
  useEffect(() => {
    if (isSavedQuoteModeRef.current) {
      setters.setHydrated(true);
      return;
    }

    const sessionExpired = cacheManager.initialize();

    if (sessionExpired) {
      setters.resetForm();
      setters.setError(null);
      setters.fetchCategories();
      setters.setHydrated(true);
      setters.setattributeError(null);
      return;
    }

    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed) {
          if (parsed.currentStep === 6) {
            setters.resetForm();
            setters.setHydrated(true);
            return;
          }
          const initialStep =
            parsed.currentStep === 1 ? 2 : parsed.currentStep ?? 2;
          setters.setCurrentStep(initialStep);

          setters.setCategoryHistory(parsed.categoryHistory ?? []);
          setters.setSelectedCategoryParent(parsed.selectedCategoryParent ?? null);
          setters.setSelectedRootCategory(parsed.selectedRootCategory ?? null);
          setters.setCurrentItem(
            parsed.currentItem ?? { category: null, attributes: {}, condition: null }
          );
          setters.setItems(parsed.items ?? []);
          setters.setIsEditing(parsed.isEditing ?? false);
          setters.setSelectedCondition(parsed.selectedCondition ?? null);
          setters.setCurrentAttributeIndex(parsed.currentAttributeIndex ?? 0);
          setters.setAttributes(parsed.attributes ?? []);
          setters.setManufacturerOptions(parsed.manufacturerOptions ?? []);
          setters.setModelOptions(parsed.modelOptions ?? []);
          setters.setDraftItem(
            parsed.draftItem ?? { category: null, attributes: {}, condition: null }
          );
          setters.setTotalEstimate(parsed.totalEstimate ?? { min: 0, max: 0 });
          setters.setquoteId(parsed.quoteId ?? null);
          setters.setBrand(parsed.brand ?? null);
          setters.setopenEstimateCard(parsed.openEstimateCard ?? false);
          setters.setOpenExpertReviewAccordion(parsed.openExpertReviewAccordion ?? false);
          setters.setCategoryItemState(
            parsed.categoryItemState && typeof parsed.categoryItemState === "object"
              ? parsed.categoryItemState
              : {}
          );
          setters.setZipCodeFromModal(parsed.zipCodeFromModal ?? null);
          setters.setNearbyStores(parsed.nearbyStores ?? []);
          setters.setSelectedStoreOption(parsed.selectedStoreOption ?? null);
          setters.setEmailSendCount(parsed.emailSendCount ?? {});
          setters.setGoldCurrentAttributeIndex(parsed.goldCurrentAttributeIndex ?? 0);
          setters.setGoldParentCategory(parsed.goldParentCategory ?? null);
          setters.setSelectedGoldItemType(parsed.selectedGoldItemType ?? null);

          // --- Recalculate currentAttributeIndex based on last filled attribute ---
          if (parsed.currentItem && parsed.attributes) {
            const lastFilledIndex = parsed.attributes
              .map((attr: any) => attr.coreAttributeName)
              .reduce((maxIdx: number, key: string, idx: number) => {
                const val = parsed.currentItem.attributes?.[key];
                return val && val.id && val.id !== "" ? idx : maxIdx;
              }, -1);
            setters.setCurrentAttributeIndex(lastFilledIndex >= 0 ? lastFilledIndex : 0);
          } else {
            setters.setCurrentAttributeIndex(parsed.currentAttributeIndex ?? 0);
          }

          if (
            initialStep === 3 &&
            parsed.currentItem &&
            parsed.currentItem.category &&
            parsed.attributes &&
            parsed.attributes.some((attr: any) => attr.coreAttributeName === "Manufacturer") &&
            (!parsed.manufacturerOptions || parsed.manufacturerOptions.length === 0)
          ) {
            setters.fetchManufacturers(parsed.currentItem.category.category_id);
          }

          if (initialStep === 2) {
            setters.fetchCategories();
          } else if (parsed.categories && parsed.categories.length > 0) {
            setters.setCategories(parsed.categories);
            setters.setSelectedCategoryParent(parsed.selectedCategoryParent || null);
          } else if (parsed.categoryHistory && parsed.categoryHistory.length > 0) {
            const lastCategories = parsed.categoryHistory[parsed.categoryHistory.length - 1];
            setters.setCategories(lastCategories);

            if (parsed.selectedCategoryParent) {
              setters.setSelectedCategoryParent(parsed.selectedCategoryParent);
            } else if (parsed.categoryHistory.length > 1) {
              const parentLevel = parsed.categoryHistory[parsed.categoryHistory.length - 2];
              if (parentLevel && parentLevel.length > 0) {
                const parent = parentLevel.find((cat: Category) =>
                  cat.childCategories && cat.childCategories.some((child: Category) =>
                    child.category_id === lastCategories[0]?.category_id
                  )
                );
                setters.setSelectedCategoryParent(parent || null);
              }
            }
          }
        }
      } catch (e) {
        console.error("Failed to load state from localStorage:", e);
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        setters.resetForm();
      }
    } else {
      setters.fetchCategories();
      setters.setCurrentStep(2);
    }
    setters.setHydrated(true);
  }, []);

  // --- Save state to localStorage whenever relevant state changes ---
  useEffect(() => {
    if (!hydrated || isSavedQuoteModeRef.current) return;
    const stateToSave = restState;
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(stateToSave));
    cacheManager.updateCacheTimestamp();
  }, [
    hydrated,
    restState.currentStep,
    restState.categories,
    restState.categoryHistory,
    restState.selectedCategoryParent,
    restState.selectedRootCategory,
    restState.currentItem,
    restState.items,
    restState.isEditing,
    restState.selectedCondition,
    restState.currentAttributeIndex,
    restState.attributes,
    restState.manufacturerOptions,
    restState.modelOptions,
    restState.draftItem,
    restState.totalEstimate,
    restState.quoteId,
    restState.brand,
    restState.openEstimateCard,
    restState.openExpertReviewAccordion,
    restState.categoryItemState,
    restState.zipCodeFromModal,
    restState.nearbyStores,
    restState.selectedStoreOption,
    restState.emailSendCount,
    restState.goldCurrentAttributeIndex,
    restState.goldParentCategory,
    restState.selectedGoldItemType,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      localStorage.removeItem(CACHE_CONSTANTS.CACHE_KEYS.STORE_INFO);
    };
  }, []);
}
